import axios from 'axios';
import i18n from '../i18n';

/**
 * Represents a generic API response structure for retrieving entity data from the server.
 * @template T - The type of the entity being retrieved.
 */
export interface ApiResponse<T> {
  data: {
    /**
     * A list of entities of type T retrieved from the server.
     */
    values: T[];
    /**
     * The total number of items available for the requested entity type.
     */
    totalItems: number;
    /**
     * The total number of pages available for pagination.
     */
    totalPages: number;
    /**
     * The current page number being returned in this response.
     */
    currentPage: number;
    /**
     * The number of items included per page in this response.
     */
    itemsPerPage: number;
  };
  /**
   * Additional messages or metadata returned by the server, if any.
   * This may include warnings, errors, or supplementary information.
   */
  messages: any | null;
}

const baseUrl: string | undefined = process.env.REACT_APP_BACK_END_API_URL;

const api = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

api.interceptors.request.use(config => {
  const lang = i18n.language || 'pt';

  let culture = lang;
  if (lang === 'pt') {
    culture = 'pt-BR';
  } else if (lang === 'en') {
    culture = 'en-US';
  }

  if (!config.headers) {
    config.headers = ({} as any);
  }

  (config.headers as any)['Accept-Language'] = culture;

  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Sessão expirou: redirecionar
      window.location.href = '/login'; 
    }
    return Promise.reject(error);
  }
);

export async function fetchData<T>(
  endpoint: string,
  params: object
): Promise<ApiResponse<T>> {
  const response = await api.get<ApiResponse<T>>(endpoint, { params });
  return response.data;
}

export default api;
